import HttpClient from './HttpClient';
import Storage from './Storage';
import Util from './Util';
import { browserHistory } from 'react-router';
import User from './model/User';
import SosdbRoleAction from './model/SosdbRoleAction';
import KioskVersionSet from './model/KioskVersionSet';
import AppInfo from './model/AppInfo';
import LogURL from './model/LogURL';
import HostInfo from './model/HostInfo';
import { ActionTypes } from './Constants';
import _ from 'lodash';

let Api = {};
Api.BASE_URL = location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '');

let http = new HttpClient(Api.BASE_URL, () => {
  return {
    token: Storage.getToken(),
    uid: Storage.getProfileEmail(),
  };
});

function handleAuthError(err) {
  if (err.statusCode === 401) {
    Api.logout();
  }
  throw err;
}

Api.status = function () {
  return http.fetchJSON('/api/status', {
    method: 'GET',
    timeout: 10000,
  });
};

Api.login = function (email, password) {
  return http
    .fetchJSON('/api/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
      }),
    })
    .then((data) => {
      Storage.setToken(data.token, data.user);
      let user = new User(data.user);
      return user;
    });
};

Api.renewToken = function () {
  return http
    .fetchJSON('/api/token', {
      method: 'POST',
    })
    .then((data) => {
      Storage.setToken(data.token, data.user);
      let user = new User(data.user);
      return user;
    })
    .catch(handleAuthError);
};

Api.logout = function () {
  Storage.clearToken();
  let redirectAfterLogin = Util.getNextPathAfterLogin();
  browserHistory.push(`/login?next=${redirectAfterLogin}`);
};

Api.getSosdbRoleActionList = function () {
  return http
    .fetchJSON('/api/sosdb/listRoleActions', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.createSosdbRoleAction = function (roleAction) {
  if (!(roleAction instanceof SosdbRoleAction)) {
    roleAction = new SosdbRoleAction(roleAction);
  }
  return http
    .fetchJSON('/api/sosdb/createRoleAction', {
      method: 'POST',
      body: JSON.stringify(roleAction.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.updateSosdbRoleAction = function (roleAction) {
  if (!(roleAction instanceof SosdbRoleAction)) {
    roleAction = new SosdbRoleAction(roleAction);
  }
  return http
    .fetchJSON('/api/sosdb/updateRoleAction', {
      method: 'POST',
      body: JSON.stringify(roleAction.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.deleteSosdbRoleAction = function (roleAction) {
  return http
    .fetchJSON('/api/sosdb/deleteRoleAction', {
      method: 'POST',
      body: JSON.stringify(roleAction.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.getUserList = function () {
  return http
    .fetchJSON('/api/systemuser/list', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.createUser = function (user) {
  if (!(user instanceof User)) {
    user = new User(user);
  }
  return http
    .fetchJSON('/api/systemuser/create', {
      method: 'POST',
      body: JSON.stringify(user.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.updateUser = function (user) {
  if (!(user instanceof User)) {
    user = new User(user);
  }
  return http
    .fetchJSON('/api/systemuser/update', {
      method: 'POST',
      body: JSON.stringify(user.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.updateProfile = function (user) {
  if (!(user instanceof User)) {
    user = new User(user);
  }
  return http
    .fetchJSON('/api/systemuser/updateprofile', {
      method: 'POST',
      body: JSON.stringify(user.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.deleteUserByEmail = function (email) {
  return http
    .fetchJSON('/api/systemuser/delete', {
      method: 'DELETE',
      qs: {
        email: email,
      },
    })
    .catch(handleAuthError);
};

Api.getUserList = function () {
  return http
    .fetchJSON('/api/systemuser/list', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.getRoleMapping = function () {
  return http
    .fetchJSON('/api/systemuser/roleMapping', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.getBuildVersions = function (project) {
  return http.fetchJSON('/api/baymax/buildversions', {
    method: 'GET',
    qs: {
      project,
    },
  });
};

Api.getArtifactoryFolderInfo = function (folder) {
  return http.fetchJSON('/api/artifactory/storage', {
    method: 'GET',
    qs: {
      folder,
    },
  });
};

function extractCiscoAmpVersion(info) {
  let versions = [];
  if (info.children && info.children.length > 0) {
    // Valid version are "ciscoampconnector-1.15.2.746-centos-7.rpm" and "ciscoampconnector-1.15.2.746-ubuntu-20-04.deb"
    // Extract that into 1.15.2.746
    versions = info.children.map((obj) =>
      obj.uri
        .replace(/^\/ciscoampconnector-/, '')
        .replace(/-centos-7.rpm$/, '')
        .replace(/-ubuntu-20-04.deb$/, '')
    );
    // remove "latest" and any package with .rpm and .deb
    versions = versions.filter((v) => !v.endsWith('.deb') && !v.endsWith('.rpm') && v !== 'latest');
    versions = _.uniq(versions); // remove duplicate in the future if we store assets in folder instead of as .tgz
    //console.log(versions);
  }

  return versions;
}

Api.getBuildVersionsFromArtifactory = function (folder) {
  return Api.getArtifactoryFolderInfo(folder).then((info) => {
    if (folder === 'cisco-amp') {
      return extractCiscoAmpVersion(info);
    }

    let versions = [];
    if (info.children && info.children.length > 0) {
      versions = info.children.map((obj) => obj.uri.replace(/^\//, '').replace(/\.tgz$/, ''));
      versions = _.uniq(versions); // remove duplicate in the future if we store assets in folder instead of as .tgz
    }

    // sort version based on major, minor and micro number
    versions.sort((a, b) => {
      // assume v1.2.3-<branch>
      let versionA = a.replace(/-.+$/, '');
      let branchA = a.replace(/^v?\d+\.\d+\.\d+/, '');
      let versionB = b.replace(/-.+$/, '');
      let branchB = b.replace(/^v?\d+\.\d+\.\d+/, '');

      // assume 21.2.1-12282280-<branch> or 21.2.1-1228ab-<branch>
      let r = /^(\d+\.\d+\.\d+-\d{4}(\d{4}|[a-z]{2}))-(.+)$/;
      if (r.test(a)) {
        let m = a.match(r);
        versionA = m[1];
        branchA = m[3];
      }
      if (r.test(b)) {
        let m = b.match(r);
        versionB = m[1];
        branchB = m[3];
      }

      let versionComp = _compareVersion(versionA, versionB);
      let branchComp = _compareString(branchA, branchB);
      if (branchComp === 0 && versionA && versionB) {
        return versionComp;
      }
      return branchComp;
    });

    return versions;
  });
};

function _compareVersion(a, b) {
  let r = /^v?(\d+)\.(\d+)\.(\d+)(-(\d{4}(\d{4}|[a-z]{2})))?$/;
  let majorA = 0;
  let minorA = 0;
  let microA = 0;
  let hhmmA = 0;
  let majorB = 0;
  let minorB = 0;
  let microB = 0;
  let hhmmB = 0;

  if (r.test(a) && r.test(b)) {
    const m1 = a.match(r);
    majorA = parseInt(m1[1], 10);
    minorA = parseInt(m1[2], 10);
    microA = parseInt(m1[3], 10);
    hhmmA = parseInt(m1[5], 10) || 0;

    const m2 = b.match(r);
    majorB = parseInt(m2[1], 10);
    minorB = parseInt(m2[2], 10);
    microB = parseInt(m2[3], 10);
    hhmmB = parseInt(m2[5], 10) || 0;

    if (majorB - majorA === 0) {
      if (minorB - minorA === 0) {
        if (microB - microA === 0) {
          return hhmmB - hhmmA;
        }
        return microB - microA;
      }
      return minorB - minorA;
    }
    return majorB - majorB;
  }

  return _compareString(a, b);
}

function _compareString(a, b) {
  if (a < b) return 1;
  if (a > b) return -1;
  return 0;
}

Api.getLoginAttemptList = function (success = false, limit = 300) {
  return http
    .fetchJSON('/api/systemuser/loginattempts', {
      method: 'GET',
      qs: {
        limit: limit,
        success: success ? 'true' : 'false',
      },
    })
    .catch(handleAuthError);
};

Api.getHostInfoList = function () {
  return http
    .fetchJSON('/api/hostinfo/list', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.getHostInfo = function (id) {
  return http
    .fetchJSON('/api/hostinfo/show', {
      method: 'GET',
      qs: {
        id,
        // ts: new Date().getTime() // prevent "304 Not Modified" browser cache
      },
    })
    .catch(handleAuthError);
};

Api.fetchInfo = function (hostInfo) {
  let kioskName = '';
  if (hostInfo.isMQKskType && !hostInfo.kskUseVpnIPAddr) {
    // use msgapi to get info if kioskName exists
    kioskName = hostInfo.kioskName;
  }
  return Api.fetchInfoByIP(hostInfo.ip, hostInfo.authType, hostInfo.fingerprint, kioskName);
};

Api.fetchInfoByIP = function (ip, authType, fingerprint = '', kioskName = '') {
  return http
    .fetchJSON('/api/fetch/info', {
      method: 'GET',
      timeout: 0, // no timeout
      qs: {
        ip,
        authType,
        fingerprint,
        kioskName,
      },
    })
    .catch(handleAuthError);
};

Api.deleteHostInfo = function (id) {
  return http
    .fetchJSON('/api/hostinfo/delete', {
      method: 'DELETE',
      qs: {
        id,
      },
    })
    .catch(handleAuthError);
};

Api.checkAlive = function (hostInfo) {
  let kioskName = '';
  if (hostInfo.isMQKskType && !hostInfo.kskUseVpnIPAddr) {
    // use msgapi to get info if kioskName exists
    kioskName = hostInfo.kioskName;
  }
  return http.fetchJSON('/api/hostinfo/checkalive', {
    timeout: 15000,
    method: 'POST',
    qs: {
      kioskName: kioskName,
      ip: hostInfo.ip,
    },
  });
};

Api.getIPInfo = function (ip) {
  return http.fetchJSON('/ip_geocode', {
    timeout: 15000,
    method: 'POST',
    qs: {
      ip,
    },
  });
};

Api.linuxdash = function (hostInfo, path, options = { timeout: 180000 }) {
  path = Util.getLinuxDashPath(hostInfo) + path;
  return http.fetchJSON(path, options);
};

Api.kskapi = function (hostInfo, path, options = { timeout: 180000 }) {
  path = Util.getKskApiPath(hostInfo) + path;
  return http.fetchJSON(path, options);
};

Api.cafeapi = function (hostInfo, path, options = { timeout: 180000 }) {
  path = Util.getCafeApiPath(hostInfo) + path;
  return http.fetchJSON(path, options);
};

Api.payapi = function (hostInfo, path, options = { timeout: 180000 }) {
  path = Util.getPayApiPath(hostInfo) + path;
  return http.fetchJSON(path, options);
};

Api.printapi = function (hostInfo, path, options = { timeout: 180000 }) {
  path = Util.getPrintApiPath(hostInfo) + path;
  return http.fetchJSON(path, options);
};

Api.capui = function (hostInfo, path, options = { timeout: 180000 }) {
  path = Util.getCapUIPath(hostInfo) + path;
  return http.fetchJSON(path, options);
};

Api.getOrgList = function () {
  return http
    .fetchJSON('/api/commission/listOrgs', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.getLocationList = function (org) {
  return http
    .fetchJSON('/api/commission/listLocations', {
      method: 'GET',
      qs: {
        org,
      },
    })
    .catch(handleAuthError);
};

Api.getSerialNumber = function (loc) {
  return http
    .fetchJSON('/api/g2convert/getserialnumber', {
      method: 'GET',
      qs: {
        loc,
      },
    })
    .catch(handleAuthError);
};

Api.startScript = function (kioskName, xthId) {
  return http
    .fetchJSON('/api/g2convert/startscript', {
      method: 'GET',
      qs: {
        kioskName,
        xthId,
      },
    })
    .catch(handleAuthError);
};

Api.checkOngoingScript = function () {
  return http
    .fetchJSON('/api/g2convert/checkHotScript', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.getSHQCusts = function () {
  return http
    .fetchJSON('/api/g2convert/getshqcusts', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.getSHQLocs = function (custid) {
  return http
    .fetchJSON('/api/g2convert/getshqlocs', {
      method: 'GET',
      qs: {
        custid,
      },
    })
    .catch(handleAuthError);
};

Api.getItemCount = function (locid) {
  return http
    .fetchJSON('/api/g2convert/getitemcount', {
      method: 'GET',
      qs: {
        locid,
      },
    })
    .catch(handleAuthError);
};

Api.getProductCount = function (locid, orgid) {
  return http
    .fetchJSON('/api/org2orgcopy/getProductCount', {
      method: 'GET',
      qs: {
        locid,
        orgid,
      },
    })
    .catch(handleAuthError);
};

Api.getRevertCopy = function (historyId) {
  return http
    .fetchJSON('/api/org2orgcopy/revertCopiedProduct', {
      method: 'GET',
      qs: {
        historyId,
      },
    })
    .catch(handleAuthError);
};

Api.copyProductsFromOrg2Org = function (
  srcOrgId,
  srcLocId,
  dstOrgId,
  dstLocId,
  isModifiersIncluded,
  isTaxesIncluded,
  isPrinterIncluded
) {
  let email = Storage.getProfileEmail();
  return http
    .fetchJSON('/api/org2orgcopy/copyProductsOrg2Org', {
      method: 'GET',
      qs: {
        srcOrgId,
        srcLocId,
        dstOrgId,
        dstLocId,
        isModifiersIncluded,
        isTaxesIncluded,
        email,
        isPrinterIncluded,
      },
    })
    .catch(handleAuthError);
};

Api.copyProducts = function (srclocid, dstlocid, useinv) {
  return http
    .fetchJSON('/api/g2convert/copyproducts', {
      method: 'GET',
      qs: {
        srclocid,
        dstlocid,
        useinv,
      },
    })
    .catch(handleAuthError);
};

Api.getKiosk = function (name) {
  return http
    .fetchJSON('/api/commission/getKiosk', {
      method: 'POST',
      qs: {
        name,
      },
    })
    .catch(handleAuthError);
};

Api.setOrg = function (name, org) {
  return http
    .fetchJSON('/api/commission/setOrg', {
      method: 'POST',
      qs: {
        name,
        org,
      },
    })
    .catch(handleAuthError);
};

Api.getKVSList = function () {
  return http
    .fetchJSON('/api/kioskversionset/list', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.getKVSHistoryList = function (start = 0, count = count) {
  return http
    .fetchJSON('/api/kioskversionset/historylist', {
      method: 'GET',
      qs: {
        start,
        count,
      },
    })
    .catch(handleAuthError);
};

Api.createKVS = function (kvs) {
  if (!(kvs instanceof KioskVersionSet)) {
    kvs = new KioskVersionSet(kvs);
  }
  return http
    .fetchJSON('/api/kioskversionset/create', {
      method: 'POST',
      body: JSON.stringify(kvs.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.updateKVS = function (kvs) {
  if (!(kvs instanceof KioskVersionSet)) {
    kvs = new KioskVersionSet(kvs);
  }
  return http
    .fetchJSON('/api/kioskversionset/update', {
      method: 'POST',
      body: JSON.stringify(kvs.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.deleteKVS = function (name, reason, updatedBy) {
  return http
    .fetchJSON('/api/kioskversionset/delete', {
      method: 'DELETE',
      qs: {
        name: name,
        reason: reason,
        updatedBy: updatedBy,
      },
    })
    .catch(handleAuthError);
};

Api.getAppInfoList = function () {
  return http
    .fetchJSON('/api/appinfo/list', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.checkAllAppInfoList = function () {
  return http
    .fetchJSON('/api/appinfo/checkall', {
      method: 'POST',
      timeout: 600000,
    })
    .catch(handleAuthError);
};

Api.createAppInfo = function (appInfo) {
  if (!(appInfo instanceof AppInfo)) {
    appInfo = new AppInfo(appInfo);
  }
  return http
    .fetchJSON('/api/appinfo/create', {
      method: 'POST',
      body: JSON.stringify(appInfo.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.updateAppInfo = function (appInfo) {
  if (!(appInfo instanceof AppInfo)) {
    appInfo = new AppInfo(appInfo);
  }
  return http
    .fetchJSON('/api/appinfo/update', {
      method: 'POST',
      body: JSON.stringify(appInfo.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.deleteAppInfo = function (id) {
  return http
    .fetchJSON('/api/appinfo/delete', {
      method: 'DELETE',
      qs: {
        id: id,
      },
    })
    .catch(handleAuthError);
};

Api.checkAppInfo = function (appInfo, create = false) {
  if (!(appInfo instanceof AppInfo)) {
    appInfo = new AppInfo(appInfo);
  }
  return http
    .fetchJSON('/api/appinfo/check', {
      method: 'POST',
      qs: {
        create: create,
      },
      body: JSON.stringify(appInfo.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.checkAllAppInfos = function () {
  return http
    .fetchJSON('/api/appinfo/checkall', {
      method: 'POST',
    })
    .catch(handleAuthError);
};

Api.getLogURLList = function () {
  return http
    .fetchJSON('/api/logurl/list', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.createLogURL = function (logURL) {
  if (!(logURL instanceof LogURL)) {
    logURL = new LogURL(logURL);
  }
  return http
    .fetchJSON('/api/logurl/create', {
      method: 'POST',
      body: JSON.stringify(logURL.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.updateLogURL = function (logURL) {
  if (!(logURL instanceof LogURL)) {
    logURL = new LogURL(logURL);
  }
  return http
    .fetchJSON('/api/logurl/update', {
      method: 'POST',
      body: JSON.stringify(logURL.toPlainObject()),
    })
    .catch(handleAuthError);
};

Api.deleteLogURL = function (id) {
  return http
    .fetchJSON('/api/logurl/delete', {
      method: 'DELETE',
      qs: {
        id: id,
      },
    })
    .catch(handleAuthError);
};

Api.getKioskUpdateHistoryList = function (hostInfo, start = '0', count = 10, lastXDays = 730) {
  return http
    .fetchJSON('/api/kioskupdatehistory/list', {
      method: 'GET',
      qs: {
        fingerprint: hostInfo.fingerprint,
        kioskId: hostInfo.kioskId,
        start,
        count,
        lastXDays,
      },
    })
    .catch(handleAuthError);
};

Api.updateSosdbKioskTag = function (kioskName, tag) {
  return http
    .fetchJSON('/api/sosdb/updateKioskTag', {
      method: 'POST',
      qs: {
        kioskName,
        tag,
      },
    })
    .catch(handleAuthError);
};

Api.updateSosdbKioskDeploymentSet = function (kioskName, deploymentSet) {
  return http
    .fetchJSON('/api/sosdb/updateKioskDeploymentSet', {
      method: 'POST',
      qs: {
        kioskName,
        deploymentSet,
      },
    })
    .catch(handleAuthError);
};

Api.updateSosdbPatchDateAndTime = function (kioskNames, patchDate, patchTime) {
  return http
    .fetchJSON('/api/sosdb/updatePatchDateAndTime', {
      method: 'POST',
      qs: {
        kioskNames,
        patchDate,
        patchTime,
      },
    })
    .catch(handleAuthError);
};

Api.updateSosdbKioskInstallUpdateTime = function (kioskName, installUpdateTime) {
  return http
    .fetchJSON('/api/sosdb/updateKioskInstallUpdateTime', {
      method: 'POST',
      qs: {
        kioskName,
        installUpdateTime,
      },
    })
    .catch(handleAuthError);
};

Api.isAlertNotificationsEnabled = function () {
  return http
    .fetchJSON('/api/sosdb/isAlertNotificationsEnabled', {
      method: 'GET',
    })
    .catch(handleAuthError);
};

Api.enableAlertNotifications = function () {
  return http
    .fetchJSON('/api/sosdb/enableAlertNotifications', {
      method: 'POST',
    })
    .catch(handleAuthError);
};

Api.disableAlertNotifications = function () {
  return http
    .fetchJSON('/api/sosdb/disableAlertNotifications', {
      method: 'POST',
    })
    .catch(handleAuthError);
};

Api.getXTemplateList = function () {
  return http
    .fetchJSON('/api/xtemplate/list', {
      method: 'GET',
      qs: {},
    })
    .catch(handleAuthError);
};

Api.deleteXTemplate = function (xth) {
  return http
    .fetchJSON('/api/xtemplate/delete', {
      method: 'DELETE',
      qs: {
        id: xth.id,
      },
    })
    .catch(handleAuthError);
};

Api.applyXTemplate = function (kioskName, xthId) {
  return http
    .fetchJSON('/api/xtemplate/applyTemplate', {
      method: 'POST',
      qs: {
        kioskName: kioskName,
        id: xthId,
      },
    })
    .catch(handleAuthError);
};

Api.createXTemplate = function (kioskName, xthName, xthDesc) {
  return http
    .fetchJSON('/api/xtemplate/create', {
      method: 'POST',
      qs: {
        kioskName: kioskName,
        name: xthName,
        desc: xthDesc,
      },
    })
    .catch(handleAuthError);
};

Api.getXTemplate = function (id) {
  return http
    .fetchJSON('/api/xtemplate/show', {
      method: 'GET',
      qs: {
        id: id,
      },
    })
    .catch(handleAuthError);
};

Api.getSosdbG2ConvertHistorySwapList = function () {
  return http
    .fetchJSON('/api/sosdb/listG2ConvertHistorySwap', {
      method: 'GET',
      qs: {},
    })
    .catch(handleAuthError);
};

Api.getSosdbG2ConvertHistoryCopyList = function () {
  return http
    .fetchJSON('/api/sosdb/listG2ConvertHistoryCopy', {
      method: 'GET',
      qs: {},
    })
    .catch(handleAuthError);
};

Api.getOrg2OrgHistoryCopyList = function () {
  return http
    .fetchJSON('/api/org2orgcopy/listOrg2OrgHistoryCopy', {
      method: 'GET',
      qs: {},
    })
    .catch(handleAuthError);
};

Api.getKioskList = function (columns) {
  return http
    .fetchJSON('/api/sosdb/kioskList', {
      method: 'GET',
      qs: {
        columns,
      },
    })
    .catch(handleAuthError);
};

Api.getQueueActionData = function () {
  return http
    .fetchJSON('/api/queue/actionData', {
      method: 'GET',
      qs: {},
    })
    .catch(handleAuthError);
};

Api.getQueueActionLog = function () {
  return http
    .fetchJSON('/api/queue/queueTaskLog', {
      method: 'GET',
      qs: {},
    })
    .catch(handleAuthError);
};

Api.sendQueueMessages = function (actionName = '', createdBy = '', kioskTasks = []) {
  return http
    .fetchJSON('/api/queue/sendMessages', {
      method: 'POST',
      qs: {
        actionName,
        createdBy,
      },
      body: JSON.stringify(kioskTasks),
    })
    .catch(handleAuthError);
};

Api.patchList = function () {
  return http.fetchJSON('/api/patch/list', { method: 'GET' }).catch(handleAuthError);
};

Api.backupList = function () {
  return http.fetchJSON('/api/patch/backuplist', { method: 'GET' }).catch(handleAuthError);
};

Api.getPatchVersion = function (repo) {
  return http
    .fetchJSON('/api/patch/getversion', { method: 'GET', qs: { repo } })
    .catch(handleAuthError);
};

Api.mirrorRepo = function () {
  return http.fetchJSON('/api/patch/mirrorrepo', { method: 'POST' }).catch(handleAuthError);
};

Api.reposync = function () {
  return http.fetchJSON('/api/patch/reposync', { method: 'POST' }).catch(handleAuthError);
};

Api.switchRepo = function (source, target) {
  return http
    .fetchJSON('/api/patch/switchrepo', { method: 'POST', qs: { source, target } })
    .catch(handleAuthError);
};

Api.recover = function (source, target) {
  return http
    .fetchJSON('/api/patch/recover', { method: 'POST', qs: { source, target } })
    .catch(handleAuthError);
};

Api.listDashBeatCommandsResult = function (fingerprint, startDate, endDate) {
  return http.fetchJSON('/api/dashbeatcommand/listResult', {
    method: 'GET',
    qs: { fingerprint, startDate, endDate },
  });
};

Api.listDashBeatCommandsInQueue = function (fingerprint) {
  return http.fetchJSON('/api/dashbeatcommand/listQueue', {
    method: 'GET',
    qs: { fingerprint },
  });
};

Api.clearDashBeatCommandsInQueue = function (fingerprint) {
  return http.fetchJSON('/api/dashbeatcommand/clearQueue', {
    method: 'DELETE',
    qs: { fingerprint },
  });
};

Api.addDashBeatCommand = function (fingerprint, commandName, commandString) {
  return http.fetchJSON('/api/dashbeatcommand/addToLast', {
    method: 'POST',
    qs: { fingerprint, commandName, commandString },
  });
};

Api.sendBackuptqEvent = function (data) {
  return http.fetchJSON('/api/kioskevent/sendbackuptqevent', {
    method: 'POST',
    qs: { data },
  });
};

Api.sendBackupccEvent = function (data) {
  return http.fetchJSON('/api/kioskevent/sendbackupcclocalevent', {
    method: 'POST',
    qs: { data },
  });
};

export default Api;



// WEBPACK FOOTER //
// ./src/Api.js